import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Card,
  CardContent,
  Box,
  IconButton,
  CardMedia,
  Chip,
  Dialog,
  DialogTitle,
  DialogActions,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { report } from 'process';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_RED,
  exchangeRates,
  FREE_REPORT_CREATION_LIMIT,
} from 'src/const';
import {
  SearchAddressDetailsType,
  ReportTypes,
  handleLoginOrPricingModalOpenType,
} from '../../propTypes';
import SearchAutoComplete from './Components/SearchAutoComplete';
import { User } from 'src/interfaces/iuser';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  checkIfSettingPage,
  checkIfUserLoggedIn,
  convertCurrency,
  formatCurrency,
  getUserPreferredCurrency,
  sortUserMarkets,
} from 'src/utils/functions';
import { callApi } from 'src/core/callApi';
import ShareLinkModal from '../../../../Components/Modals/ShareLinkModal';
import SortTypeSelectionMenu from 'src/App/HOC/SortTypeSelectionMenu';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import InfoTooltipContent from 'src/App/HOC/Tooltips/InfoTooltipContent';
import { REPORT_TYPE_TOOLTIPS } from 'src/tooltips';
import CompareIcon from 'src/App/assets/compareIcon.svg';
import { useSetUserPaymentInterest } from 'src/hooks/useCustomHooks';

const styles = {
  root: (matches: boolean) => ({
    paddingBottom: matches ? '270px' : '70px',
    background: matches ? '#E7F2F4' : '#FFF',
  }),
  searchBarContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    height: '50%',
  },
  reportTypeButtonGroup: {
    borderRadius: '900px',
    background: '#E7F2F4',
    width: 'auto',
    padding: '5px 7px',
    marginY: '20px',
  },
  reportTypeButton: (buttonType: ReportTypes, reportType: ReportTypes) => ({
    '&:hover': {
      background: '#fff',
    },
    borderRadius: '900px',
    background: buttonType === reportType ? '#fff' : 'inherit',
    color: buttonType === reportType ? COLOR_PRIMARY_DARK : '#000',
  }),
  searchBarRoot: {
    padding: '5px',
    height: '90px',
    maxWidth: '1600px',
    borderRadius: '12px',
    background: '#E7F2F4',
  },
  searchBarSunSectionContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    background: '#fff',
    border: '6px solid #E7F2F4',
    borderRadius: '12px',
    height: '100%',
  },
  searchInputLabel: {
    position: 'absolute',
    top: '5px',
    left: '16px',
    fontSize: '14px',
    color: '#8B92A5',
    fontWeight: 500,
  },
  bedroomCounterContainer: {
    marginLeft: '16px',
    marginTop: '20px',
  },
  bedroomCounterButton: {
    border: '1px solid #000',
    height: '24px',
    width: '24px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  calculateButton: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    color: '#fff',
    background: COLOR_PRIMARY_DARK,
    fontSize: '18px',
  },
};

const SearchBarReportTypeSection = ({
  reportType,
  handleReportTypeChange,
}: {
  reportType: ReportTypes;
  handleReportTypeChange: (reportType: ReportTypes) => void;
}) => {
  return (
    <Grid container item sx={styles.reportTypeButtonGroup}>
      <Grid item>
        <InfoTooltip title={<InfoTooltipContent content={REPORT_TYPE_TOOLTIPS['buy']} />}>
          <Button
            onClick={() => handleReportTypeChange('buy')}
            sx={styles.reportTypeButton('buy', reportType)}
          >
            Buy
          </Button>
        </InfoTooltip>
      </Grid>
      <Grid item>
        <InfoTooltip title={<InfoTooltipContent content={REPORT_TYPE_TOOLTIPS['rent']} />}>
          <Button
            onClick={() => handleReportTypeChange('rent')}
            sx={styles.reportTypeButton('rent', reportType)}
          >
            Rent
          </Button>
        </InfoTooltip>
      </Grid>
      <Grid item>
        <InfoTooltip title={<InfoTooltipContent content={REPORT_TYPE_TOOLTIPS['manage']} />}>
          <Button
            onClick={() => handleReportTypeChange('manage')}
            sx={styles.reportTypeButton('manage', reportType)}
          >
            Manage
          </Button>
        </InfoTooltip>
      </Grid>
    </Grid>
  );
};

const SearchBar = ({
  user,
  matches,
  currency,
  dummyMarket,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  handleReport,
  handleLoginOrPricingModalOpen,
  userReportCreationCount,
}: {
  user: User;
  matches: boolean;
  currency: string;
  dummyMarket: any;
  googleAutocompleteSessionToken: any;
  getGoogleAutocompleteSessionToken: any;
  clearGoogleAutocompleteSessionToken: any;
  googleAutocompleteSessionTokenTimeStamp: any;
  handleReport: (method: any, requestBody: null | any, id?: string) => Promise<void>;
  handleLoginOrPricingModalOpen: handleLoginOrPricingModalOpenType;
  userReportCreationCount: number;
}) => {
  const isUserLoggedIn: boolean = checkIfUserLoggedIn(user.email);
  const isUserVerified: boolean = user.isVerified;

  const [reportType, setReportType] = useState<ReportTypes>('buy');

  const [addressDetails, setAddressDetails] = useState<SearchAddressDetailsType | null>(null);

  const [bedroomCount, setBedroomCount] = useState<number>(1);
  const [propertyOrRentPrice, setPropertyOrRentPrice] = useState<number | null>(null);

  const { triggerPaymentInterest } = useSetUserPaymentInterest();

  useEffect(() => {
    // check if already last selected report type is saved in local storage
    const lastSelectedReportType = localStorage.getItem('last_selected_report_type');
    if (lastSelectedReportType) {
      handleReportTypeChange(lastSelectedReportType as ReportTypes);
      return;
    }

    // check if landing report type is saved in local storage
    const landingReportType = localStorage.getItem('landing_report_type');
    if (landingReportType) {
      handleReportTypeChange(landingReportType as ReportTypes);
      localStorage.removeItem('landing_report_type');
      return;
    }

    // check user type (investor / manager etc)
    const userType = user.userDescription;
    if (userType && userType.toLowerCase() != 'trial') {
      if (userType.includes('investor')) {
        handleReportTypeChange('buy');
        return;
      } else {
        handleReportTypeChange('manage');
        return;
      }
    }
  }, [user]);

  const handleReportTypeChange = (reportType: ReportTypes) => {
    setReportType(reportType);
    localStorage.setItem('last_selected_report_type', reportType);
  };

  const handleBedroomCount = (type: 'increase' | 'decrease') => {
    if (type === 'increase') {
      setBedroomCount(prev => Math.min(prev + 1, 6));
    }
    if (type === 'decrease') {
      setBedroomCount(prev => Math.max(prev - 1, 0));
    }
  };

  const handlePropertyOrRentPriceChange = (price: string) => {
    price = price.replace(/,/gi, '');
    if (price === '' || isNaN(Number(price))) {
      price = '0';
    }
    setPropertyOrRentPrice(Number(price));
  };

  const [searchBarInputGridSizeObject, setSearchBarInputGridSizeObject] = useState<{
    autoComplete: number;
    bedroomCounter: number;
    propertyPrice: number;
    calculateButton: number;
  }>({
    autoComplete: 6,
    bedroomCounter: 2,
    propertyPrice: 2,
    calculateButton: 2,
  });

  const handleOnCalculateClick = () => {
    if (addressDetails === null) {
      return;
    }

    let currentReportType = reportType;

    const ApiRequestObject: { [key: string]: string | number | null } = {
      address_full: addressDetails.address_full,
      address_short: addressDetails.address_short,
      latitude: addressDetails.latitude,
      longitude: addressDetails.longitude,
      bedrooms: bedroomCount,
      report_type: reportType.toLowerCase(),
      property_price: propertyOrRentPrice,
      monthly_rent: propertyOrRentPrice,
    };

    if (
      reportType === 'buy' &&
      (!ApiRequestObject.property_price || ApiRequestObject.property_price == 0)
    ) {
      setReportType('manage');
      currentReportType = 'manage';
    }

    if (
      reportType === 'rent' &&
      (!ApiRequestObject.monthly_rent || ApiRequestObject.monthly_rent == 0)
    ) {
      setReportType('manage');
      currentReportType = 'manage';
    }

    if (currentReportType === 'manage') {
      delete ApiRequestObject.property_price;
      delete ApiRequestObject.monthly_rent;
      ApiRequestObject.report_type = 'manage';
    }

    if (currentReportType === 'buy') {
      delete ApiRequestObject.monthly_rent;
    }

    if (currentReportType === 'rent') {
      delete ApiRequestObject.property_price;
    }

    if (!isUserLoggedIn) {
      handleLoginOrPricingModalOpen('open');
      localStorage.setItem('estimator-create-report', JSON.stringify(ApiRequestObject));
      return;
    }

    if (!isUserVerified) {
      handleLoginOrPricingModalOpen('open', 'emailVerify');
      return;
    }

    if (
      user.subscriptionPlan == 'starter' &&
      userReportCreationCount > FREE_REPORT_CREATION_LIMIT
    ) {  
      handleLoginOrPricingModalOpen('open', 'payment');
      return;
    }

    handleReport('CREATE', ApiRequestObject);
  };

  useEffect(() => {
    const newSearchBarInputGridSizeObject = {
      autoComplete: matches ? 12 : 6,
      bedroomCounter: matches ? 12 : 2,
      propertyPrice: matches ? 12 : 2,
      calculateButton: matches ? 12 : 2,
    };

    if (reportType == 'manage') {
      newSearchBarInputGridSizeObject.autoComplete = matches ? 12 : 7;
      newSearchBarInputGridSizeObject.bedroomCounter = matches ? 12 : 3;
      newSearchBarInputGridSizeObject.propertyPrice = 0;
    }

    setSearchBarInputGridSizeObject(newSearchBarInputGridSizeObject);
  }, [reportType, matches]);

  return (
    <Grid container item sx={styles.searchBarContainer} xs={12}>
      <SearchBarReportTypeSection
        reportType={reportType}
        handleReportTypeChange={handleReportTypeChange}
      />
      <Grid container item justifyContent="center" xs={12}>
        <Grid item container sx={styles.searchBarRoot}>
          <Grid
            item
            xs={searchBarInputGridSizeObject.autoComplete}
            sx={styles.searchBarSunSectionContainer}
          >
            <Typography variant="body1" sx={styles.searchInputLabel}>
              Address
            </Typography>
            <SearchAutoComplete
              user={user}
              matches={matches}
              dummyMarket={dummyMarket}
              googleAutocompleteSessionToken={googleAutocompleteSessionToken}
              getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
              clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
              googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
              setAddressDetails={setAddressDetails}
            />
          </Grid>

          <Grid
            item
            xs={searchBarInputGridSizeObject.bedroomCounter}
            sx={styles.searchBarSunSectionContainer}
          >
            <Typography variant="body1" sx={styles.searchInputLabel}>
              Bedrooms
            </Typography>

            <Grid container alignItems="center" sx={styles.bedroomCounterContainer}>
              <Grid
                item
                sx={styles.bedroomCounterButton}
                onClick={() => handleBedroomCount('decrease')}
              >
                <RemoveRoundedIcon />
              </Grid>
              <Typography
                variant="body1"
                sx={{ fontSize: '16px', marginLeft: '10px', marginRight: '10px' }}
              >
                {bedroomCount === 0 ? 'Studio' : bedroomCount === 6 ? '6+' : bedroomCount}
              </Typography>
              <Grid
                item
                sx={styles.bedroomCounterButton}
                onClick={() => handleBedroomCount('increase')}
              >
                <AddRoundedIcon />
              </Grid>
            </Grid>
          </Grid>

          {searchBarInputGridSizeObject.propertyPrice !== 0 && (
            <Grid
              item
              xs={searchBarInputGridSizeObject.propertyPrice}
              sx={styles.searchBarSunSectionContainer}
            >
              <Typography variant="body1" sx={styles.searchInputLabel}>
                Property {reportType === 'buy' ? 'Price' : 'rent'}
              </Typography>
              <Grid container alignItems="center" sx={styles.bedroomCounterContainer}>
                <TextField
                  variant="standard"
                  value={
                    propertyOrRentPrice === null ? '' : formatCurrency(String(propertyOrRentPrice))
                  }
                  onChange={e => handlePropertyOrRentPriceChange(e.target.value)}
                  sx={{ marginRight: '10px' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {exchangeRates[currency].symbol}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}

          <Grid
            item
            xs={searchBarInputGridSizeObject.calculateButton}
            sx={styles.searchBarSunSectionContainer}
          >
            <Button
              sx={styles.calculateButton}
              disabled={addressDetails === null ? true : false}
              onClick={handleOnCalculateClick}
            >
              Calculate
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const getCurrentSavedReportDisplayKpi = (report, currency: string) => {
  const reportType = report.report_type;

  switch (reportType) {
    case 'buy':
      const annualRateOfReturn = report.annual_return_rate;
      return ['Annual Rate of Return', annualRateOfReturn, false];
    case 'rent':
      const monthlyProfit = convertCurrency(currency, report.monthly_profit);
      return ['Monthly Profit', monthlyProfit, true];
    case 'manage':
      const yearlyRevenue = convertCurrency(currency, report.ltm_revenue);
      return ['Yearly Revenue', yearlyRevenue, true];
    default:
      return ['', '', false];
  }
};

const MenuShareDelete = ({
  urlPath,
  currentReportCardId,
  isOptionsOpen,
  setIsOptionsOpen,
  setComingSoonModalOpen,
  handleDeleteReport,
}) => {
  const menuItemStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
  };

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isShareLinkModalOpen, setIsShareLinkModalOpen] = useState<boolean>(false);

  return (
    <>
      <Menu
        id="lock-menu"
        anchorEl={document.getElementById(currentReportCardId)}
        open={isOptionsOpen}
        onClose={() => setIsOptionsOpen(false)}
      >
        <MenuItem
          onClick={() => {
            setIsShareLinkModalOpen(true);
            logAmplitudeEvent('report_shared', { source: 'report_card' });
          }}
        >
          <Box sx={menuItemStyle}>
            <ReplyRoundedIcon sx={{ transform: 'scaleX(-1)' }} />
            Share
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setIsDeleteDialogOpen(true);
          }}
        >
          <Box sx={menuItemStyle}>
            <DeleteOutlineRoundedIcon /> Delete
          </Box>
        </MenuItem>
      </Menu>

      <Dialog open={isDeleteDialogOpen}>
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete this report?
        </DialogTitle>
        <DialogActions sx={{ padding: '20px' }}>
          <Button
            variant="outlined"
            color="info"
            startIcon={<CloseRoundedIcon />}
            onClick={() => setIsDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={handleDeleteReport}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <ShareLinkModal
        open={isShareLinkModalOpen}
        setOpen={setIsShareLinkModalOpen}
        shareUrl={`${window.location.origin}/${urlPath}`}
      />
    </>
  );
};

const SavedReportCard = ({
  report,
  currency,
  handleReport,
  setComingSoonModalOpen,
  onlyRenderSavedReports,
}) => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  const currentReportCardId = 'report-card-' + report.id;
  const urlPath = report.url_path;

  const handleSavedReportClick = () => {
    if (onlyRenderSavedReports) {
      window.open('/' + urlPath);
    } else {
      handleReport('READ', null, report.id);
    }
  };

  const handleDeleteReport = async () => {
    handleReport('DELETE', null, report.id);
    setIsOptionsOpen(false);
  };

  const handleOptionClick = event => {
    event.stopPropagation();
    setIsOptionsOpen(true);
  };

  const [displayKpiName, displayKpiValue, isValueCurrency] = getCurrentSavedReportDisplayKpi(
    report,
    currency
  );

  const address: string = report.address;

  return (
    <>
      <Card
        sx={{
          position: 'relative',
          display: 'flex',
          minWidth: '300px',
          maxWidth: '400px',
          width: '100%',
          cursor: 'pointer',
        }}
        onClick={handleSavedReportClick}
      >
        <CardMedia
          component="img"
          sx={{ width: '30%' }}
          image="https://airbtics-wordpress-market-assets.s3.us-east-2.amazonaws.com/map-image-no-label-demo.png"
        />
        <Chip
          label={report.report_type}
          sx={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: '#fff',
            border: '1px solid ' + COLOR_PRIMARY_DARK,
            color: COLOR_PRIMARY_DARK,
            fontWeight: 'bold',
            textTransform: 'capitalize',
          }}
        />
        <Button
          onClick={handleOptionClick}
          id={currentReportCardId}
          size="small"
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            background: '#fff',
            color: COLOR_PRIMARY_DARK,
            width: '20px',
            maxWidth: '20px',
            minWidth: '20px',
          }}
        >
          <MoreVertIcon />
        </Button>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <CardContent>
            <Typography component="div" variant="subtitle1">
              {displayKpiName}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {isValueCurrency && exchangeRates[currency].symbol}
              {isValueCurrency ? formatCurrency(String(displayKpiValue)) : displayKpiValue}
              {!isValueCurrency && ' %'}
            </Typography>
          </CardContent>
          <CardContent sx={{ paddingBottom: '10 !important' }}>
            <Typography variant="subtitle1" sx={{ alignSelf: 'flex-end', fontSize: '14px' }}>
              {address.slice(0, 25)}
              {address.length > 25 && '...'}
            </Typography>
          </CardContent>
        </Box>
      </Card>

      <MenuShareDelete
        urlPath={urlPath}
        currentReportCardId={currentReportCardId}
        isOptionsOpen={isOptionsOpen}
        setIsOptionsOpen={setIsOptionsOpen}
        setComingSoonModalOpen={setComingSoonModalOpen}
        handleDeleteReport={handleDeleteReport}
      />
    </>
  );
};

const SavedReports = ({
  isLoading,
  reports,
  currency,
  handleReport,
  setComingSoonModalOpen,
  setReportComparePageOpen,
}) => {
  const [sortingOrder, setSortingOrder] = useState<
    'alpha-a-z' | 'alpha-z-a' | 'date-a-z' | 'date-z-a' | undefined
  >(undefined);
  const [sortKey, setSortKey] = useState<'address' | 'created_at'>('created_at');
  const sortedReports =
    reports && (sortingOrder ? sortUserMarkets(reports, sortKey, sortingOrder) : reports);

  const onlyRenderSavedReports = checkIfSettingPage();

  return (
    <Grid
      container
      item
      xs
      sx={{
        width: '100%',
        backgroundColor: onlyRenderSavedReports ? '#fff' : '#E7F2F4',
        minHeight: '100px',
        borderTopLeftRadius: '40px',
        marginTop: onlyRenderSavedReports ? 0 : '-30px',
      }}
    >
      <Grid
        container
        item
        xs
        sx={{
          width: '100%',
          background: '#fff',
          marginTop: '20px',
          borderTopLeftRadius: '40px',
          padding: '20px',
        }}
      >
        {isLoading && <SavedCompsLoader />}

        {!isLoading && sortedReports && (
          <>
            <Grid container sx={{mb: '10px'}} item xs={12}>
              {!onlyRenderSavedReports && (
                <>
                  <Grid item xs={8}>
                    <Typography variant="h5" sx={{ fontWeight: 500 }}>
                      Your Reports
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Button variant='outlined' startIcon={<img style={{width: '20px', height: '20px', color: "#3AAFA9"}} src={CompareIcon} />} onClick={setReportComparePageOpen} sx={{float: 'right'}}>Compare</Button>
                  </Grid>
                </>
              )}
            </Grid>

            <Grid item xs={12} sx={{ marginBottom: '-20px' }}>
              <SortTypeSelectionMenu
                optionsMap={{
                  'date-a-z': {
                    displayName: 'Created date New - Old',
                    sortKey: 'created_at',
                  },
                  'date-z-a': {
                    displayName: 'Created date Old - New',
                    sortKey: 'created_at',
                  },
                  'alpha-a-z': {
                    displayName: 'Location Name A-Z',
                    sortKey: 'address',
                  },
                  'alpha-z-a': {
                    displayName: 'Location Name Z-A',
                    sortKey: 'address',
                  },
                }}
                sortingOrder={sortingOrder}
                setStateOnChange={setSortingOrder}
                setSortKey={setSortKey}
              />
            </Grid>

            <Grid container item spacing={4} sx={{ marginTop: '20px' }}>
              {sortedReports.map((report, index) => (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={index}>
                  <SavedReportCard
                    report={report}
                    currency={currency}
                    handleReport={handleReport}
                    setComingSoonModalOpen={setComingSoonModalOpen}
                    onlyRenderSavedReports={onlyRenderSavedReports}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};

const SavedCompsLoader = () => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '200px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <CircularProgress size={40} />
    </Box>
  );
};

const SearchPage = ({
  user,
  matches,
  currency,
  dummyMarket,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  handleReport,
  handleLoginOrPricingModalOpen,
  setComingSoonModalOpen,
  savedReports,
  setSavedReports,
  resetCurrencyToDefault,
  setReportComparePageOpen,
}: {
  user: User;
  matches: boolean;
  currency: string;
  dummyMarket: any;
  googleAutocompleteSessionToken: any;
  getGoogleAutocompleteSessionToken: any;
  clearGoogleAutocompleteSessionToken: any;
  googleAutocompleteSessionTokenTimeStamp: any;
  handleReport: (method: any, requestBody: null | any, id?: string) => Promise<void>;
  handleLoginOrPricingModalOpen: handleLoginOrPricingModalOpenType;
  setComingSoonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  savedReports: { data: null; loading: boolean };
  setSavedReports: React.Dispatch<React.SetStateAction<{ data: null; loading: boolean }>>;
  resetCurrencyToDefault: () => void;
  setReportComparePageOpen;
}) => {
  const onlyRenderSavedReports = checkIfSettingPage();

  const [userReportCreationCount, setUserReportCreationCount] = useState<number>(0);
  const getSavedReports = async () => {
    setSavedReports({ data: null, loading: true });
    const url =
      'https://3iugob4wql.execute-api.us-east-2.amazonaws.com/prod/airbnbCalculatorReportCRUD';
    const data = await callApi(url, {}, true, false, '', 'GET', true);
    data !== 'Unauthenticated request'
      ? setSavedReports({ data: data.reports, loading: false })
      : setSavedReports({ data: null, loading: false });
    setUserReportCreationCount(data.count);
  };

  useEffect(() => {
    getSavedReports();
    logAmplitudeEvent('report_landing_page_visited');
    resetCurrencyToDefault();
  }, []);

  return (
    <>
      {!onlyRenderSavedReports && (
        <Grid container sx={styles.root(matches)} xs={12}>
          <SearchBar
            user={user}
            matches={matches}
            currency={currency}
            dummyMarket={dummyMarket}
            googleAutocompleteSessionToken={googleAutocompleteSessionToken}
            getGoogleAutocompleteSessionToken={getGoogleAutocompleteSessionToken}
            clearGoogleAutocompleteSessionToken={clearGoogleAutocompleteSessionToken}
            googleAutocompleteSessionTokenTimeStamp={googleAutocompleteSessionTokenTimeStamp}
            handleReport={handleReport}
            handleLoginOrPricingModalOpen={handleLoginOrPricingModalOpen}
            userReportCreationCount={userReportCreationCount}
          />
        </Grid>
      )}

      <SavedReports
        isLoading={savedReports.loading}
        reports={savedReports.data}
        currency={currency}
        handleReport={handleReport}
        setComingSoonModalOpen={setComingSoonModalOpen}
        setReportComparePageOpen={setReportComparePageOpen}
      />
    </>
  );
};

export default SearchPage;
